import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Cpu, ShieldCheck, Zap, Globe, GitBranch } from 'lucide-react';

const Lattice = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const features = [
    { 
      title: "Intent-Based Orchestration", 
      desc: "Transform high-level network intent into surgical, multi-vendor configurations automatically.",
      icon: Layers
    },
    { 
      title: "HFT-Grade Precision", 
      desc: "Architected for sub-microsecond latency environments with 17-step technical registry gating.",
      icon: Zap
    },
    { 
      title: "Forensic Registry", 
      desc: "Zero-loss archival of configuration states, change requests, and validation logs (§14.6 TTR).",
      icon: ShieldCheck
    },
    { 
      title: "Multi-Vendor Translation", 
      desc: "Seamless normalization across Arista EOS, Cisco IOS, Palo Alto, and FIX protocol layers.",
      icon: Cpu
    },
    { 
      title: "Global Infrastructure Map", 
      desc: "Manage multi-site datacenters (LD4, NY4, HK1) with unified IPAM and asset tracking.",
      icon: Globe
    },
    { 
      title: "Simulation-First Loop", 
      desc: "Push topology states to VegaFlux for physics-level validation before physical deployment.",
      icon: GitBranch
    }
  ];

  return (
    <div className="pt-24 min-h-screen bg-transparent">
      {/* --- HERO SECTION --- */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[hsl(var(--primary-gold))]/10 rounded-full blur-[100px] -mr-64 -mt-64"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[hsl(var(--peacock-blue))]/10 rounded-full blur-[100px] -ml-64 -mb-64"></div>

        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="brand-subtitle mb-4">The Orchestration Hub</h2>
            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              <span className="gradient-text">LATTICE</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Industrializing the fintech lifecycle with the Premier Infrastructure Hub. 
              Designed for trillion-dollar workflows and sub-microsecond precision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* --- FEATURES GRID --- */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((f, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                className="glass-effect p-8 rounded-2xl border border-[hsl(var(--border))]/30 hover:border-[hsl(var(--primary-gold))]/50 transition-all duration-300 group hover:shadow-[0_0_30px_rgba(153,101,21,0.1)]"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--border))]/20 flex items-center justify-center mb-6 border border-[hsl(var(--border))]/50 group-hover:neon-glow transition-all duration-300">
                  <f.icon className="w-7 h-7 text-[hsl(var(--primary-gold))]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 brand-title text-white">{f.title}</h3>
                <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                  {f.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* --- 17-STEP PROCESS ANCHOR --- */}
      <section className="py-20 bg-[hsl(var(--card))]/30 relative overflow-hidden">
         <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl font-bold mb-8 brand-title">Powered by the 17-Step E2E Protocol</h2>
              <div className="brand-separator mx-auto max-w-[200px] mb-8"></div>
              <p className="text-lg text-gray-400 mb-12 italic">
                "Technical precision is not an option; it is built into the registry."
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-medium tracking-widest text-[hsl(var(--primary-gold))] opacity-80 uppercase">
                 <span>Intake</span>
                 <span>Design</span>
                 <span>Translate</span>
                 <span>Verify</span>
              </div>
            </motion.div>
         </div>
      </section>
      
      {/* Footer Buffer */}
      <div className="py-20"></div>
    </div>
  );
};

export default Lattice;
